import React, { useEffect, useRef, useState } from 'react';
import { inject } from 'mobx-react';
import { useClassName } from 'common/hooks';
import { ContentfulRichText, SVGIcon } from 'common/components';
import { Loader } from 'common/lazy';

const DEFAULT_BRAZE_TRIGGERS = {
  customAttributes: {
    groupsales: 'yes',
  },
  customEvent: 'subscription groupsales form',
};
import ProgressBar from 'public/components/ProgressBar';
import FormStep from './FormStep';
import ThankYou from './ThankTou';
import { track } from 'utils/analytics';
import { DEFAULT_GROUP_ID, SMS_GROUP_ID } from 'common/const';
import './form.less';

const Form = ({ component, formSubmission, subscriptions }) => {
  const classNames = useClassName('CLPForm');
  const formRef = useRef(null);
  const {
    steps,
    sendToMonday,
    sendToBraze,
    brazeCustomAttribute,
    brazeCustomEvent,
  } = component.fields;
  const [currentStep, setCurrentStep] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [insideModal, setInsideModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const totalSteps = steps.filter((step) => !step.fields.isThankYouStep).length;

  useEffect(() => {
    const formElement = formRef?.current;
    if (!formElement?.parentElement?.className?.includes('--modal')) return;
    setInsideModal(true);
    const cleanFormData = () => {
      setTimeout(() => {
        if (!formElement?.parentElement?.classList?.contains('open')) {
          setFormValues({});
          setCurrentStep(0);
        }
      }, 500);
    };
    const observer = new MutationObserver(() => cleanFormData());
    observer.observe(formElement.parentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  const goPrev = () => setCurrentStep((prev) => prev - 1);

  const sendBrazeForm = async () => {
    const brazeFields = Object.keys(formValues).reduce((acc, key) => {
      const { brazeName, value } = formValues[key];
      if (!brazeName || !value) return acc;
      acc[brazeName] = value;
      return acc;
    }, {});

    if (!Object.keys(brazeFields).length) return;

    const brazeTriggers = {
      ...DEFAULT_BRAZE_TRIGGERS,
      ...(brazeCustomAttribute && {
        customAttributes: { [brazeCustomAttribute]: 'yes' },
      }),
      ...(brazeCustomEvent && { customEvent: brazeCustomEvent }),
    };

    track('Set Properties', {
      ...brazeFields,
      ...brazeTriggers?.customAttributes,
    });

    if (brazeTriggers?.customEvent) {
      track(brazeTriggers.customEvent);
    }

    if (brazeFields.emailAccepted) {
      await subscriptions.create({ ...brazeFields, group: DEFAULT_GROUP_ID });
    }
    if (brazeFields.smsAccepted) {
      await subscriptions.create({ ...brazeFields, group: SMS_GROUP_ID });
    }
  };

  const sendMondayForm = async () =>
    await formSubmission.sendToMonday(
      Object.keys(formValues)
        .map((key) => {
          const { mondayName, value, type } = formValues[key];
          if (!mondayName || !value || !type) return null;
          return {
            key: mondayName,
            value,
            type,
          };
        })
        .filter(Boolean),
      true
    );

  const saveFormSubmission = async (metadata = {}) => {
    const formData = Object.keys(formValues).reduce((acc, key) => {
      acc[key] = formValues[key].value;
      return acc;
    }, {});

    await formSubmission.save(formData, location.pathname, metadata);
  };

  const goNext = async () => {
    if (steps[currentStep + 1]?.fields?.isThankYouStep) {
      if (isSubmitting) return;
      setIsSubmitting(true);

      const metadata = {
        sentToMonday: false,
        brazeCustomEvent: null,
        brazeCustomAttribute: null,
        mondayError: null,
      };

      try {
        if (sendToMonday) {
          try {
            await sendMondayForm();
            metadata.sentToMonday = true;
          } catch (err) {
            metadata.mondayError = err.message;
            console.error('Monday error:', err);
          }
        }

        if (sendToBraze) {
          try {
            await sendBrazeForm();
            // Only set braze metadata after successful submission
            const brazeTriggers = {
              ...DEFAULT_BRAZE_TRIGGERS,
              ...(brazeCustomAttribute && {
                customAttributes: { [brazeCustomAttribute]: 'yes' },
              }),
              ...(brazeCustomEvent && { customEvent: brazeCustomEvent }),
            };
            if (brazeTriggers.customEvent) {
              metadata.brazeCustomEvent = brazeTriggers.customEvent;
            }
            if (brazeTriggers.customAttributes) {
              const attrKeys = Object.keys(brazeTriggers.customAttributes);
              if (attrKeys.length > 0) {
                metadata.brazeCustomAttribute = attrKeys[0];
              }
            }
          } catch (err) {
            console.error('Braze error:', err);
          }
        }

        await saveFormSubmission(metadata);
      } finally {
        setIsSubmitting(false);
      }
    }
    if (isLastStep) return;
    setCurrentStep((prev) => prev + 1);
  };

  if (steps[currentStep].fields.isThankYouStep) {
    return <ThankYou step={steps[currentStep]} insideModal={insideModal} />;
  }

  const requiredFields = steps[currentStep].fields.fields
    .filter((f) => f.fields.required)
    .map((f) => f.fields.name);

  const canContinue = requiredFields.every((field) =>
    Boolean(formValues[field]?.value)
  );

  const handleInputChange = ({ name, value, mondayName, brazeName, type }) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: {
        value,
        mondayName,
        brazeName,
        type,
      },
    }));
  };

  return (
    <div ref={formRef} className={classNames('container')}>
      <div className={classNames('title-container')}>
        <p className={classNames('title')}>{component.fields.title}</p>
        <section className={classNames('description')}>
          <ContentfulRichText
            field={{ 'en-US': component.fields.description }}
          />
        </section>
      </div>
      <div className={classNames('wizard-container')}>
        <div className={classNames('steps-container')}>
          <ProgressBar
            totalSteps={totalSteps}
            currentStep={currentStep + 1}
            stepName={steps[currentStep].fields.name}
          />
          <div
            className={classNames('steps-wizard')}
            style={{
              transform: `translateX(-${currentStep * 100}%)`,
              transition: 'transform 0.4s ease',
            }}>
            {steps?.map((step, index) => (
              <FormStep
                key={`step-${index}`}
                handleInputChange={handleInputChange}
                component={step}
                formValues={formValues}
              />
            ))}
          </div>
        </div>
        <div className={classNames('controls')}>
          {!requiredFields.length && (
            <button className={classNames('btn')} onClick={goNext}>
              Skip
            </button>
          )}
          <div className={classNames('direction-controls')}>
            {!isFirstStep && (
              <button className={classNames('btn')} onClick={goPrev}>
                <SVGIcon name="slider-arrow-left" size="tiny" />
                Prev
              </button>
            )}
            <button
              className={classNames(
                'btn',
                !canContinue || isSubmitting ? 'disabled' : 'enabled'
              )}
              onClick={goNext}
              disabled={!canContinue || isSubmitting}>
              {currentStep === steps.length - 2 ? (
                isSubmitting ? (
                  <Loader active inline size="tiny" />
                ) : (
                  'Submit'
                )
              ) : (
                <>
                  Next
                  <SVGIcon name="slider-arrow-right" size="tiny" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default inject('formSubmission', 'subscriptions')(Form);
